import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGlobal } from '../../Context/GlobalContext';
import { useWorkspace } from '../../Context/WorkspaceContext';
import { Alert, Spinner, Form } from 'react-bootstrap';
import { PrimaryButton, CancelButton, DangerButton } from '../../Components/Buttons';
import { getAppDisplayName, getAppColor } from './bvUtils';
import { useLanguage } from '../../Context/LanguageContext';
import { useGrandfatheredAccess } from '../../hooks/useGrandfatheredAccess';
import { FiBook } from 'react-icons/fi';
import supportIcon from '../../assets/characters/support-gray.webp';
import imageGenIcon from '../../assets/characters/image-gen-gray.webp';
import socialMediaIcon from '../../assets/characters/social-media-gray.webp';
import videoGenIcon from '../../assets/characters/video-gray.webp';
import seoIcon from '../../assets/characters/seo-gray.webp';
import leadIcon from '../../assets/characters/lead-gray.webp';
import './training-process.css';

const PAGE_SIZE = 10;

// Helper function to get app icon
const getAppIcon = (appName) => {
  const name = appName.toLowerCase();
  if (name.includes('video')) return videoGenIcon;
  if (name.includes('image') || name.includes('gen')) return imageGenIcon;
  if (name.includes('social')) return socialMediaIcon;
  if (name.includes('seo')) return seoIcon;
  if (name.includes('lead')) return leadIcon;
  return supportIcon; // fallback
};

// Helper function to get color filter for app icons
const getAppIconFilter = (appName) => {
  const name = appName.toLowerCase();
  if (name.includes('video')) {
    // Video Gen BV - Cyan tint (#0D5A6F)
    return 'brightness(0.95) sepia(0.15) hue-rotate(180deg) saturate(1.5)';
  }
  if (name.includes('image') || name.includes('gen')) {
    // Image Gen BV - Orange/Brown tint (#752E16)
    return 'brightness(0.95) sepia(0.15) hue-rotate(15deg) saturate(1.5)';
  }
  if (name.includes('social')) {
    // Social Media BV - Blue tint (#1B3576)
    return 'brightness(0.95) sepia(0.15) hue-rotate(210deg) saturate(1.5)';
  }
  if (name.includes('seo')) {
    // SEO BV - Red tint (#6F1C1B)
    return 'brightness(0.95) sepia(0.15) hue-rotate(350deg) saturate(1.5)';
  }
  if (name.includes('lead')) {
    // Lead BV - Green tint (#167516)
    return 'brightness(0.95) sepia(0.15) hue-rotate(120deg) saturate(1.5)';
  }
  // Fallback - Purple tint for support
  return 'brightness(0.95) sepia(0.15) hue-rotate(240deg) saturate(1.5)';
};

const TrainProcess = (props) => {
  const { t } = useLanguage();
  const { apiUrl } = useGlobal();
  useParams(); // Component receives ID via props
  const navigate = useNavigate();
  const { hasSeoBVAccess, hasLeadBVAccess } = useGrandfatheredAccess();

  // Get workspace data for limits and chatbots
  const { workspaceData, currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.workspace_id;
  const workspaceCharacterLimit = workspaceData?.workspace_character_count_limit || null;

  // Memoize chatbots array to prevent unnecessary re-renders
  const workspaceChatbots = useMemo(() => {
    return workspaceData?.chatbots || [];
  }, [workspaceData?.chatbots]);

  const [trainingItems, setTrainingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [isTraining, setIsTraining] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [totalNewCharacterCount, setTotalNewCharacterCount] = useState(0);

  const [userCharacterCountLoading, setUserCharacterCountLoading] =
    useState(true);

  // Pagination and search states
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  // Chatbot selection states
  const [selectedChatbots, setSelectedChatbots] = useState([]);

  // Apps states
  const [apps, setApps] = useState([]);
  const [selectedApps, setSelectedApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);

  // Memoize filtered apps to prevent unnecessary re-renders
  const filteredApps = useMemo(() => {
    return apps.filter(app => {
      const name = app.name?.toLowerCase() || '';
      if (name.includes('seo') && !hasSeoBVAccess) return false;
      if (name.includes('lead') && !hasLeadBVAccess) return false;
      return true;
    });
  }, [apps, hasSeoBVAccess, hasLeadBVAccess]);

  // Memoize chatbot IDs to prevent effect re-runs
  const chatbotIds = useMemo(() => {
    return workspaceChatbots.map(c => c.chatbot_id).join(',');
  }, [workspaceChatbots]);

  // Memoize app IDs to prevent effect re-runs
  const appIds = useMemo(() => {
    return filteredApps.map(a => a.app_id).join(',');
  }, [filteredApps]);

  // Ref to track if initial fetch has been done
  const hasFetchedRef = useRef(false);

  // Fetch available apps
  useEffect(() => {
    const fetchApps = async () => {
      try {
        setAppsLoading(true);
        const brandvoxToken = localStorage.getItem('brandvox_token');
        const response = await fetch(
          `${apiUrl}/api/v1/apps/get-apps`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${brandvoxToken}`,
            }
          }
        );

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        setApps(data.apps || []);
      } catch (err) {
      } finally {
        setAppsLoading(false);
      }
    };

    fetchApps();
  }, [apiUrl]);

  useEffect(() => {
    // Prevent duplicate fetches - only fetch once per workspace
    if (!workspaceId || hasFetchedRef.current) {
      return;
    }

    const fetchTrainingData = async () => {
      try {
        hasFetchedRef.current = true;
        setLoading(true);
        const brandvoxToken = localStorage.getItem('brandvox_token');
        const response = await fetch(
          `${apiUrl}/api/v1/train/get-training-data`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${brandvoxToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ workspace_id: workspaceId }),
          }
        );
        if (!response.ok) {
          throw new Error(t('dashboard.training.trainprocess.errorTrainFailed'));
        }
        const data = await response.json();
        const items = Object.values(data.training_data);
        setTrainingItems(items);
        setTotalNewCharacterCount(data.total_new_character_count || 0);
      } catch (err) {
        setError(err.message);
        hasFetchedRef.current = false; // Allow retry on error
      } finally {
        setLoading(false);
      }
    };
    fetchTrainingData();
  }, [workspaceId, apiUrl, t]);

  // Select all chatbots when workspace data is available (only on initial load)
  useEffect(() => {
    if (chatbotIds) {
      setSelectedChatbots(prev => {
        // Only set if not already selected (initial load)
        if (prev.length === 0) {
          return chatbotIds.split(',').filter(id => id);
        }
        return prev;
      });
    }
  }, [chatbotIds]);

  // Select all apps when they finish loading (only on initial load)
  useEffect(() => {
    if (appIds) {
      setSelectedApps(prev => {
        // Only set if not already selected (initial load)
        if (prev.length === 0) {
          return appIds.split(',').filter(id => id);
        }
        return prev;
      });
    }
  }, [appIds]);

  useEffect(() => {
    // Since we're using workspace limits, just mark loading as complete
    // when workspace data is available
    if (workspaceCharacterLimit !== null) {
      setUserCharacterCountLoading(false);
    }
  }, [workspaceCharacterLimit]);

  // Filter items based on search term
  const filteredItems = trainingItems.filter(item =>
    item.key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredItems.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const itemsToShow = filteredItems.slice(startIndex, startIndex + PAGE_SIZE);

  // Smart pagination - show at most 5 pages
  const getVisiblePages = () => {
    const maxVisible = 3;
    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const half = Math.floor(maxVisible / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisible - 1);

    // Adjust start if we're near the end
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    const pages = [];

    // Always show page 1
    if (start > 1) {
      pages.push(1);
      if (start > 2) {
        pages.push('...');
      }
    }

    // Show the range
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Always show last page
    if (end < totalPages) {
      if (end < totalPages - 1) {
        pages.push('...');
      }
      pages.push(totalPages);
    }

    return pages;
  };

  const visiblePages = getVisiblePages();

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Update to reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Handle deletion of an item.
  const handleDeleteItem = (itemKey) => {
    const itemToDelete = trainingItems.find((item) => item.key === itemKey);
    if (!itemToDelete) {
      return;
    }
    setTrainingItems((prev) => {
      const filtered = prev.filter((item) => item.key !== itemKey);
      if (filtered.length === 0) {
        handleCancelTraining();
      }
      return filtered;
    });
    setTotalNewCharacterCount((prevCount) => prevCount - itemToDelete.size);

    // Check if current page will be empty after deletion and navigate to previous page if needed
    const updatedFilteredItems = trainingItems
      .filter(item => item.key !== itemKey)
      .filter(item => item.key.toLowerCase().includes(searchTerm.toLowerCase()));

    const updatedTotalPages = Math.ceil(updatedFilteredItems.length / PAGE_SIZE);

    // If current page will be empty and there are other pages, go to previous page
    if (currentPage > 1 && currentPage > updatedTotalPages) {
      setCurrentPage(Math.max(1, updatedTotalPages));
    }
  };

  // Handle final training.
  const handleTrain = async () => {
    if (totalNewCharacterCount > workspaceCharacterLimit) {
      alert(t('dashboard.training.trainprocess.errorOverLimit'));
      return;
    }

    try {
      setIsTraining(true);
      const brandvoxToken = localStorage.getItem('brandvox_token');

      // Build the assignment mapping for selected chatbots and apps
      const assignedTo = {
        chatbots: selectedChatbots,
        apps: selectedApps
      };

      const body = JSON.stringify({
        workspace_id: workspaceId,
        content_names: trainingItems.map((item) => item.key),
        assigned_to: assignedTo
      });

      const response = await fetch(`${apiUrl}/api/v1/train/train-data`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body,
      });

      if (!response.ok) {
        throw new Error(t('dashboard.training.trainprocess.errorTrainFailed'));
      }

      if (props.onTrainSuccess) {
        await props.onTrainSuccess();
      }

      if (props.onClose) {
        props.onClose();
      } else {
        navigate(`/workspace/training`);
      }
    } catch (err) {
      setError(err.message);
      setIsTraining(false);
    }
  };

  // Handle cancel training.
  const handleCancelTraining = async () => {
    try {
      setIsCanceling(true);
      const brandvoxToken = localStorage.getItem('brandvox_token');
      const response = await fetch(`${apiUrl}/api/v1/train/cancel-training`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ workspace_id: workspaceId }),
      });
      if (!response.ok) {
        throw new Error(t('dashboard.training.trainprocess.errorCancelFailed'));
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (props.onPollState) {
        await props.onPollState();
      }
      if (props.onClose) {
        props.onClose();
      } else {
        navigate(`/workspace/training`);
      }
    } catch (err) {
      setError(err.message);
      setIsCanceling(false);
    }
  };

  if (loading) {
    return (
      <div className='text-center my-5'>
        <Spinner animation='border' />
        <p>{t('dashboard.training.trainprocess.loadingContent')}</p>
      </div>
    );
  }

  if (error) {
    return <div className='text-center my-5 text-danger'>{error}</div>;
  }

  return (
    <div className='training-process-container' style={{ height: '90vh', display: 'flex', flexDirection: 'column' }}>
      {/* Professional header */}
      <div className='training-process-header'>
        <div className='header-content'>
          <div className='header-text'>
            <h3>{t('dashboard.training.trainprocess.selectTrainingContent')}</h3>
          </div>
          <div className='character-count-display'>
            {userCharacterCountLoading ? (
              <Spinner animation='border' size='sm' />
            ) : (
              <div
                className='d-flex align-items-center'
              >
                <strong>
                  {totalNewCharacterCount} / {workspaceCharacterLimit}
                </strong>
                <span className='ms-2 d-none d-sm-inline'>
                  {t('dashboard.training.trainprocess.contentUsed')}
                </span>
                <span className='ms-2 d-inline d-sm-none'>
                  {t('dashboard.training.trainprocess.used')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className='training-process-content'>
        {totalNewCharacterCount > workspaceCharacterLimit && (
          <Alert variant='warning'>
            {t('dashboard.training.trainprocess.overLimitWarning')}
          </Alert>
        )}

        <p className="instruction-text">
          {t('dashboard.training.trainprocess.instructionText')}
        </p>

        {/* Chatbot Selection Section */}
        {workspaceChatbots.length > 0 && (
          <div className="chatbot-selection-section mb-4">
            <div className="selection-header">
              <h5>{t('dashboard.training.trainprocess.selectSupportBVs')}</h5>
              <div className="selection-actions">
                <button
                  type="button"
                  className="selection-action-btn"
                  onClick={() => setSelectedChatbots(workspaceChatbots.map(chatbot => chatbot.chatbot_id))}
                >
                  {t('dashboard.training.trainprocess.selectAllButton')}
                </button>
                <button
                  type="button"
                  className="selection-action-btn"
                  onClick={() => setSelectedChatbots([])}
                >
                  {t('dashboard.training.trainprocess.clearAllButton')}
                </button>
              </div>
            </div>
            <p className="selection-description">
              {t('dashboard.training.trainprocess.supportBVsDescription')}
            </p>
            <div className="chatbot-grid">
              {workspaceChatbots.map(chatbot => {
                const isSelected = selectedChatbots.includes(chatbot.chatbot_id);
                return (
                  <label
                    key={chatbot.chatbot_id}
                    className={`chatbot-option support-bv ${isSelected ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedChatbots(prev => [...prev, chatbot.chatbot_id]);
                        } else {
                          setSelectedChatbots(prev => prev.filter(id => id !== chatbot.chatbot_id));
                        }
                      }}
                    />
                    <img
                      src={supportIcon}
                      alt="Support BV"
                      className="bv-icon me-2"
                      style={{
                        width: '24px',
                        height: '24px',
                        filter: isSelected ? 'brightness(0.95) sepia(0.15) hue-rotate(240deg) saturate(1.5)' : 'none',
                        transition: 'filter 0.2s ease'
                      }}
                    />
                    <span className="chatbot-name">{chatbot.chatbot_name}</span>
                    <span className="checkmark">✓</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Apps Selection Section */}
        {filteredApps.length > 0 && (
          <div className="chatbot-selection-section mb-4">
            <div className="selection-header">
              <h5>{t('dashboard.training.trainprocess.selectOtherBVs')}</h5>
              <div className="selection-actions">
                <button
                  type="button"
                  className="selection-action-btn"
                  onClick={() => setSelectedApps(filteredApps.map(app => app.app_id))}
                  disabled={appsLoading}
                >
                  {t('dashboard.training.trainprocess.selectAllButton')}
                </button>
                <button
                  type="button"
                  className="selection-action-btn"
                  onClick={() => setSelectedApps([])}
                  disabled={appsLoading}
                >
                  {t('dashboard.training.trainprocess.clearAllButton')}
                </button>
              </div>
            </div>
            <p className="selection-description">
              {t('dashboard.training.trainprocess.otherBVsDescription')}
            </p>
            {appsLoading ? (
              <div className='text-center my-3'>
                <Spinner animation='border' size='sm' />
                <p>{t('dashboard.training.trainprocess.loadingBVs')}</p>
              </div>
            ) : (
              <div className="chatbot-grid">
                {filteredApps.map(app => {
                  const appColor = getAppColor(app.name);
                  const isSelected = selectedApps.includes(app.app_id);
                  const appIcon = getAppIcon(app.name);
                  const appIconFilter = getAppIconFilter(app.name);
                  return (
                    <label
                      key={app.app_id}
                      className={`chatbot-option app-bv ${isSelected ? 'selected' : ''}`}
                      style={isSelected ? {
                        borderColor: appColor,
                        background: `${appColor}14`,
                        boxShadow: `0 0 0 2px ${appColor}1A`
                      } : {}}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedApps(prev => [...prev, app.app_id]);
                          } else {
                            setSelectedApps(prev => prev.filter(id => id !== app.app_id));
                          }
                        }}
                      />
                      <img
                        src={appIcon}
                        alt={app.name}
                        className="bv-icon me-2"
                        style={{
                          width: '24px',
                          height: '24px',
                          filter: isSelected ? appIconFilter : 'none',
                          transition: 'filter 0.2s ease'
                        }}
                      />
                      <span className="chatbot-name" style={isSelected ? { color: appColor } : {}}>
                        {getAppDisplayName(app.name, t)}
                      </span>
                      <span className="checkmark" style={isSelected ? {
                        background: appColor,
                        borderColor: appColor
                      } : {}}>✓</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Search bar */}
        <Form.Group className='mb-3'>
          <Form.Control
            type='text'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('dashboard.training.trainprocess.searchContent')}
            className='search-input'
          />
        </Form.Group>

        {trainingItems.length === 0 ? (
          <p className="no-items-message">{t('dashboard.training.trainprocess.noContentSelected')}</p>
        ) : (
          <>
            <div className="training-items-container">
              {itemsToShow.map((item) => (
                <div
                  key={item.key}
                  className='training-item'
                >
                  <div className="item-info">
                    <div className="item-key">{item.key}</div>
                    <div className="item-size">
                      {item.size.toLocaleString()} {t('dashboard.training.trainprocess.charsOfContent')}
                    </div>
                  </div>
                  <DangerButton
                    onClick={() => handleDeleteItem(item.key)}
                    size="sm"
                    title={t('dashboard.training.trainprocess.deleteTooltip')}
                    width="36px"
                    height="36px"
                    padding="0"
                    fontSize="18px"
                  >
                    ×
                  </DangerButton>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className='d-flex justify-content-center'>
                <nav aria-label="Training items pagination">
                  <ul className='pagination custom-pagination pagination-sm flex-wrap'>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className='page-link'
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                      >
                        &laquo;
                      </button>
                    </li>
                    {visiblePages.map((page, index) => (
                      <li
                        key={`${page}-${index}`}
                        className={`page-item ${page === '...' ? 'disabled' :
                          currentPage === page ? 'active' : ''
                          }`}
                      >
                        <button
                          className='page-link'
                          onClick={() => page !== '...' && handlePageChange(page)}
                          disabled={page === '...'}
                          aria-label={page === '...' ? 'More pages' : `Page ${page}`}
                        >
                          {page}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        className='page-link'
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        aria-label="Next page"
                      >
                        &raquo;
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </>
        )}

      </div>

      {/* Action buttons footer */}
      <div className='training-process-footer'>
        <CancelButton
          onClick={handleCancelTraining}
          disabled={isTraining || isCanceling}
          style={{ marginRight: '8px' }}
        >
          {t('dashboard.training.trainprocess.cancelTraining')}
        </CancelButton>
        <PrimaryButton
          onClick={handleTrain}
          disabled={
            trainingItems.length === 0 ||
            isTraining ||
            isCanceling ||
            totalNewCharacterCount > workspaceCharacterLimit
          }
        >
          {isCanceling
            ? t('dashboard.training.trainprocess.canceling')
            : isTraining
              ? t('dashboard.training.trainprocess.teachingBrandVox')
              : <><FiBook className="me-2" />{t('dashboard.training.trainprocess.startTraining')}</>}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default TrainProcess;
